<template>
  <QuickViewModal :aria-label="product?.name || 'Товар'" @close="$emit('close')">
    <div v-if="pending" class="pqv__loading"><Spinner /></div>
    <div v-else-if="product" class="pqv">
      <div class="pqv__gallery">
        <Card type="image" :slider="slider" :show-navigation="slider.length > 1" />
      </div>
      <div class="pqv__content">
        <h3 class="pqv__title">{{ product.name }}</h3>

        <div v-if="displayPrice > 0" class="pqv__price">
          <span v-if="displayOldPrice > 0" class="pqv__price-old">{{ displayOldPrice }}&nbsp;₽</span>
          {{ displayPrice }}&nbsp;₽
        </div>

        <div v-if="showSizeSelector" class="pqv__sizes">
          <div class="pqv__sizes-title">{{ sizeAttributeName }}:</div>
          <div class="pqv__sizes-row">
            <button
              v-for="item in sizedItems"
              :key="item.id"
              type="button"
              class="size-btn"
              :class="{
                'size-btn--active': selectedItemId === item.id,
                'size-btn--disabled': item.stock <= 0,
              }"
              :disabled="item.stock <= 0"
              @click.prevent="selectSize(item)"
            >{{ item.attributeValue.value }}</button>
          </div>
          <div v-if="!selectedItemId" class="pqv__sizes-hint">Выберите размер</div>
        </div>

        <div class="pqv__desc info" v-html="product.description" />

        <div class="pqv__actions">
          <button type="button" class="pqv__cta" :disabled="!canAddToCart" @click="onCta">{{ ctaTitle }}</button>
          <a :href="productPageUrl(product)" target="_blank" rel="noopener" class="pqv__link">
            Перейти на страницу
          </a>
        </div>
      </div>
    </div>
    <div v-else class="pqv__empty">Товар не найден</div>
  </QuickViewModal>

  <Teleport to="body">
    <Form
      v-if="isOpenForm && product"
      :product-id="product.id"
      :product-type="product.type"
      @close="isOpenForm = false"
    />
  </Teleport>
</template>

<script setup lang="ts">
import QuickViewModal from '~/components/UI/QuickViewModal.vue'
import Card from '~/components/UI/Card/Card.vue'
import Form from '~/components/Modals/Form.vue'
import Spinner from '~/components/animate/SpinnerComponent.vue'
import { Product } from '~/api/Product'
import { CartHelper } from '~/helpers/CartHelper'
import { CartService } from '~/services/CartService'
import { ProductType } from '~/enums/ProductType'
import { ProductItem } from '~/api/ProductItem'
import type { ProductItemInterface } from '~/api/ProductItem'

const props = defineProps<{ id: number }>()
defineEmits(['close'])

// Модалка товара, открытая с вайба, несёт ?vibe=<id> в URL (ТЗ-006) —
// это и есть вайб для атрибуции позиции корзины (ТЗ-017).
const route = useRoute()
const vibeId = computed(() => {
  const v = route.query.vibe
  return typeof v === 'string' && v ? Number(v) : null
})

// useAsyncData (not top-level await) so this still works when the component
// is mounted client-side after a click, outside any <Suspense> boundary.
// The JSON round-trip strips Product/File's class prototypes down to plain
// data — devalue (Nuxt's SSR payload serializer) can't stringify class
// instances and would 500 the whole page on a direct deep-link otherwise.
const { data: product, pending } = useAsyncData(
  () => `product-quickview-${props.id}`,
  async () => {
    const p = await Product.getInfo(props.id).catch(() => null)
    return p ? JSON.parse(JSON.stringify(p)) : null
  }
)

// Product's `.images`/`.imgPath` are class getters that don't survive the
// SSR->client payload round-trip — read the underlying plain field instead.
const slider = computed(() => {
  const p = product.value as any
  if (!p) return []
  return (p.files ?? [])
    .filter((f: any) => f.isImage)
    .map((f: any) => ({ id: f.id, src: f.path }))
})

const isOpenForm = ref(false)
const cart = ref<any>(null)

// ── Размерный селектор — тот же приём, что в ProductCard.vue (полная
// карточка товара): цена/остаток уже встроены в ответ /product-items.
const items = ref<ProductItemInterface[]>([])
const selectedItemId = ref<number | null>(null)

const sizedItems = computed(() => items.value.filter((i) => i.attributeValue))
const showSizeSelector = computed(() => sizedItems.value.length > 0)
const sizeAttributeName = computed(() => sizedItems.value[0]?.attribute?.name ?? 'Размер')
const selectedItem = computed(
  () => items.value.find((i) => i.id === selectedItemId.value) ?? null
)

const displayPrice = computed(
  () => selectedItem.value?.price?.retailPrice ?? product.value?.price ?? 0
)
const displayOldPrice = computed(
  () => selectedItem.value?.price?.oldPrice ?? product.value?.priceOld ?? 0
)

async function fetchItems() {
  if (!product.value) return
  try {
    const data = await ProductItem.getByProduct(product.value.id)
    items.value = data.length === 1 && !data[0].attributeValue ? [] : data
  } catch {
    items.value = []
  }
}

function selectSize(item: ProductItemInterface) {
  if (item.stock <= 0) return
  selectedItemId.value = item.id
}

onMounted(() => {
  if (process.client) cart.value = CartHelper.getCart().value
  fetchItems()
})
// product.value only resolves once useAsyncData settles — re-fetch items
// once it does, in case the modal was already mounted with pending=true.
watch(product, () => fetchItems())

const canAddToCart = computed(() => {
  if (!product.value) return false
  if (product.value.type !== ProductType.PRODUCT) return true
  return !showSizeSelector.value || !!selectedItemId.value
})

const inCart = computed(() =>
  Boolean(
    product.value &&
    cart.value?.findProduct(
      product.value.id,
      product.value.id,
      showSizeSelector.value ? selectedItemId.value : null,
      vibeId.value,
    )
  )
)

const ctaTitle = computed(() => {
  if (!product.value) return ''
  if (showSizeSelector.value && !selectedItemId.value) return 'Выберите размер'
  if (product.value.type === ProductType.PRODUCT && inCart.value) return 'В корзине'
  return product.value.buttonTitle
})

function onCta() {
  if (!product.value) return
  if (product.value.type !== ProductType.PRODUCT) {
    isOpenForm.value = true
    return
  }
  if (!canAddToCart.value) return
  if (inCart.value) return

  const itemId = showSizeSelector.value ? selectedItemId.value : null
  const item = selectedItem.value
  CartService.getInstance().addProduct(
    product.value.id,
    product.value.name,
    displayPrice.value,
    product.value.id,
    1,
    itemId,
    item?.attribute?.name ?? null,
    item?.attributeValue?.value ?? null,
    vibeId.value,
  )
}
</script>

<style scoped>
.pqv {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.4rem;
  padding: 2.4rem;
}
.pqv__gallery {
  background: #f2f2f0;
  /* Definite height via aspect-ratio, not 100% of an auto-sized grid row —
     the latter creates a circular sizing loop that blows up to ~33554432px. */
  aspect-ratio: 3 / 4;
  max-height: 82vh;
  overflow: hidden;
  border-radius: 8px;
}
.pqv__gallery :deep(.card),
.pqv__gallery :deep(.swiper),
.pqv__gallery :deep(.swiper-slide) {
  height: 100%;
}
.pqv__gallery :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.pqv__content {
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
}

.pqv__title {
  font-size: 2.4rem;
  font-weight: 500;
  color: #1b1918;
  line-height: 1.3;
  margin: 0 0 1.2rem;
}

.pqv__price {
  font-size: 2rem;
  font-weight: 600;
  color: #1b1918;
  margin-bottom: 2rem;
}
.pqv__price-old {
  font-size: 1.4rem;
  font-weight: 400;
  color: #999;
  text-decoration: line-through;
  margin-right: 0.8rem;
}

.pqv__desc {
  font-size: 1.4rem;
  line-height: 1.65;
  color: #555;
  margin-bottom: 3.2rem;
  overflow-y: auto;
}

.pqv__sizes {
  margin-bottom: 2rem;
}
.pqv__sizes-title {
  font-size: 1.3rem;
  color: #555;
  margin-bottom: 0.8rem;
}
.pqv__sizes-row {
  display: flex;
  flex-wrap: wrap;
}
.pqv__sizes-hint {
  font-size: 1.3rem;
  color: #b00;
  margin-top: 0.4rem;
}
.size-btn {
  min-width: 4.4rem;
  height: 4.4rem;
  padding: 0 1.2rem;
  margin: 0 0.8rem 0.8rem 0;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  background: transparent;
  font-size: 1.4rem;
  cursor: pointer;
  transition: border-color 0.2s, opacity 0.2s;
}
.size-btn--active {
  border-color: #1b1918;
  border-width: 2px;
}
.size-btn--disabled,
.size-btn:disabled {
  color: rgba(0, 0, 0, 0.3);
  border-color: rgba(0, 0, 0, 0.08);
  text-decoration: line-through;
  cursor: not-allowed;
}
.pqv__cta:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pqv__actions {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.pqv__cta {
  padding: 1.5rem 3rem;
  background: #1b1918;
  color: #fff;
  border: none;
  border-radius: 2px;
  font-size: 1.4rem;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: opacity 0.2s;
}
.pqv__cta:hover {
  opacity: 0.85;
}

.pqv__link {
  font-size: 1.3rem;
  color: #888;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-align: center;
  transition: color 0.2s;
}
.pqv__link:hover {
  color: #1b1918;
}

.pqv__empty,
.pqv__loading {
  padding: 6rem 4rem;
  text-align: center;
  color: #888;
  font-size: 1.5rem;
  min-width: 40rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 48em) {
  .pqv__empty,
  .pqv__loading {
    min-width: 0;
  }
}

@media (max-width: 48em) {
  .pqv {
    grid-template-columns: 1fr;
    gap: 1.6rem;
    padding: 1.6rem;
  }
  .pqv__gallery {
    aspect-ratio: 4 / 3;
  }
  .pqv__content {
    padding: 1rem 0 0;
  }
}
</style>
